import type { MagicArea } from './magic-area.js';
import type { Zone } from '../janus-engine/inventory/floor-move.js';
import { occupancyOverlap } from '../../../ft3d/engine/ft3d-move-core.js';
type Point = { x: number; y: number };
const EPS = 1e-7;
const cross = (a: Point, b: Point, c: Point): number => (b.x-a.x)*(c.y-a.y)-(b.y-a.y)*(c.x-a.x);
function segmentDistance(p: Point, a: Point, b: Point): number {
  const dx=b.x-a.x, dy=b.y-a.y, len=dx*dx+dy*dy;
  const t=len ? Math.max(0,Math.min(1,((p.x-a.x)*dx+(p.y-a.y)*dy)/len)) : 0;
  return Math.hypot(p.x-a.x-t*dx,p.y-a.y-t*dy);
}
function segmentsTouch(a: Point,b: Point,c: Point,d: Point): boolean {
  if ([segmentDistance(a,c,d),segmentDistance(b,c,d),segmentDistance(c,a,b),segmentDistance(d,a,b)].some(n=>n<EPS)) return true;
  return cross(a,b,c)*cross(a,b,d)<0 && cross(c,d,a)*cross(c,d,b)<0;
}
function corners(box: Extract<Zone,{kind:'oblong'}>): Point[] {
  const c=Math.cos(box.facing),s=Math.sin(box.facing);
  return [[-1,-1],[1,-1],[1,1],[-1,1]].map(([x,y])=>({x:box.x+x!*box.halfW*c-y!*box.halfD*s,y:box.y+x!*box.halfW*s+y!*box.halfD*c}));
}
const edges = (points: Point[]): Array<[Point,Point]> => points.map((p,i)=>[p,points[(i+1)%points.length]!]);
function pointInBox(p: Point,box: Extract<Zone,{kind:'oblong'}>): boolean {
  const dx=p.x-box.x,dy=p.y-box.y,c=Math.cos(box.facing),s=Math.sin(box.facing);
  return Math.abs(dx*c+dy*s)<=box.halfW+EPS && Math.abs(-dx*s+dy*c)<=box.halfD+EPS;
}
function inSector(p: Point,area: MagicArea): boolean {
  const dx=p.x-area.origin.x,dy=p.y-area.origin.y,dist=Math.hypot(dx,dy);
  const ax=area.end.x-area.origin.x,ay=area.end.y-area.origin.y;
  return dist<=area.distance+EPS && (dist<EPS || (dx*ax+dy*ay)/Math.hypot(ax,ay)/dist>=Math.SQRT1_2-EPS);
}
function sectorEdges(area: MagicArea): Array<[Point,Point]> {
  const angle=Math.atan2(area.end.y-area.origin.y,area.end.x-area.origin.x);
  return [-Math.PI/4,Math.PI/4].map(delta=>[area.origin,{x:area.origin.x+area.distance*Math.cos(angle+delta),y:area.origin.y+area.distance*Math.sin(angle+delta)}]);
}
function circleSector(p: Point,r: number,area: MagicArea): boolean {
  if (inSector(p,area)) return true;
  if (sectorEdges(area).some(([a,b])=>segmentDistance(p,a,b)<=r+EPS)) return true;
  const dist=Math.hypot(p.x-area.origin.x,p.y-area.origin.y);
  const nearest={x:area.origin.x+(p.x-area.origin.x)*area.distance/dist,y:area.origin.y+(p.y-area.origin.y)*area.distance/dist};
  return dist>0 && inSector(nearest,area) && Math.abs(dist-area.distance)<=r+EPS;
}
function boxSector(box: Extract<Zone,{kind:'oblong'}>,area: MagicArea): boolean {
  const points=corners(box);
  if (pointInBox(area.origin,box) || points.some(p=>inSector(p,area))) return true;
  for (const [a,b] of edges(points)) {
    if (sectorEdges(area).some(([c,d])=>segmentsTouch(a,b,c,d))) return true;
    // Intersections of the circular arc with this box edge.
    const dx=b.x-a.x,dy=b.y-a.y,px=a.x-area.origin.x,py=a.y-area.origin.y;
    const A=dx*dx+dy*dy,B=2*(px*dx+py*dy),C=px*px+py*py-area.distance*area.distance,D=B*B-4*A*C;
    if (A>0 && D>=0) for(const t of [(-B-Math.sqrt(D))/(2*A),(-B+Math.sqrt(D))/(2*A)])
      if(t>=0 && t<=1 && inSector({x:a.x+t*dx,y:a.y+t*dy},area)) return true;
  }
  return false;
}
/** Exact ground footprint for authored radial/box surfaces; sphere radius is sliced at its floor. */
export function surfaceAreaFootprint(area: MagicArea): Zone {
  const x=(area.origin.x+area.end.x)/2,y=(area.origin.y+area.end.y)/2,z=Math.min(area.origin.z,area.end.z);
  if (area.shape==='square'||area.shape==='cube') return {kind:'oblong',x,y,halfW:area.distance/2,halfD:area.distance/2,
    facing:Math.atan2(area.end.y-area.origin.y,area.end.x-area.origin.x),zMin:z,zMax:z+0.01};
  return {kind:'circle',x,y,r:area.distance,zMin:z,zMax:z+0.01};
}
/** Reach a physical floor patch without replacing its footprint with a bounding circle. */
export function surfaceAreaContact(source: Zone,area: MagicArea): boolean {
  const floor=source.zMin,lo=Math.min(area.origin.z,area.end.z),hi=Math.max(area.origin.z,area.end.z);
  const line=area.shape==='line',directional=line||area.shape==='cone';
  const bottom=lo-(line?area.height/2:0)-(directional?6:0),top=hi+(line?area.height/2:area.shape==='cube'?area.distance:area.height);
  if(area.shape!=='sphere' && (floor<bottom-EPS||floor>top+EPS)) return false;
  if(area.shape==='cone') return source.kind==='circle' ? circleSector(source,source.r,area) : boxSector(source,area);
  if(line) {
    const radius=Math.max(0.5,area.depth/2),a=area.origin,b=area.end;
    if(source.kind==='circle') return segmentDistance(source,a,b)<=source.r+radius+EPS;
    if(pointInBox(a,source)||pointInBox(b,source)) return true;
    return edges(corners(source)).some(([c,d])=>segmentsTouch(a,b,c,d)
      || Math.min(segmentDistance(a,c,d),segmentDistance(b,c,d),segmentDistance(c,a,b),segmentDistance(d,a,b))<=radius+EPS);
  }
  let query=surfaceAreaFootprint(area);
  if(area.shape==='sphere') {
    const dz=Math.abs(floor-(area.origin.z+area.end.z)/2);
    if(dz>area.distance) return false;
    query={kind:'circle',x:query.x,y:query.y,r:Math.sqrt(area.distance*area.distance-dz*dz),zMin:floor-1,zMax:floor+1};
  } else query={...query,zMin:floor-1,zMax:floor+1};
  return occupancyOverlap(source,query);
}
